import { isNumber, isObject, isString } from "underscore";
import numeral from "numeral";

import { PROCESSING_CLIP_THUMBNAIL } from "const/images";

export const GrabStatus = {
    MBR: `waiting_for_mbr_transcoding`,
    WAITING_FOR_CS: `waiting_for_creative_set`,
    COMPLETE: `complete`,
    FAILED: `failed`,
    PROCESSING: `processing`,
    LIVE_BROADCAST_IN_PROGRESS: `live_broadcast_in_progress`
};

export const MediaType = {
    GIF: `gif`,
    IMAGE: `image`,
    CLIP: `clip`,
    AUDIO: `audio`,
    DEFAULT: `clip`,
    WEBRTC: `webrtc`
};

export const isActive = (props) =>
    !!props.activeItemId &&
    props.activeItemId === props.grabId &&
    props.grabStatus !== `processing`;

export const getFormattedViewCount = (viewCount = 0) => {
    if (isNumber(viewCount) && viewCount > 0) {
        return numeral(viewCount).format(`0,0`);
    } else {
        return `0`;
    }
};

export const isRawClip = (attrs = {}) => {
    const hasOriginalHttpPlaybackUrl =
        attrs.originalHttpPlaybackUrl && attrs.originalHttpPlaybackUrl.length;
    const hasHttpPlaybackUrl =
        attrs.httpPlaybackUrl && attrs.httpPlaybackUrl.length;
    const hasRawHttpPlaybackUrl =
        attrs.rawHttpPlaybackUrl && attrs.rawHttpPlaybackUrl.length;

    if (hasHttpPlaybackUrl && hasOriginalHttpPlaybackUrl) {
        return false;
    } else if (hasRawHttpPlaybackUrl) {
        return true;
    } else {
        // We end up here if none of those playback url property
        // are set, it should never happen. If it does, it's a bug
        return false;
    }
};

export const isEditable = (attrs = {}) =>
    attrs.shareCount === 0 && !isRawClip(attrs.media);

export const getDuration = (attrs = {}) => {
    if (isRawClip(attrs)) {
        return attrs.rawFileDuration;
    } else if (isNumber(attrs.originalFileDuration)) {
        return attrs.originalFileDuration;
    } else if (isNumber(attrs.originalFileDurationInMilliSeconds)) {
        return attrs.originalFileDurationInMilliSeconds;
    } else {
        return 0;
    }
};

export const formatShareCount = (attrs = {}) => {
    const isSocialShareAttribute = /shareVia(.*)Count/;
    return Object.keys(attrs)
        .filter((key) => key.match(isSocialShareAttribute) && attrs[key] > 0)
        .map((key) => {
            const name = key.match(isSocialShareAttribute)[1].toLowerCase();
            return { name, id: `${attrs.shareId}-${name}` };
        });
};

export const isSbrSuccessful = (status) => {
    switch (status) {
        case GrabStatus.PROCESSING:
        case GrabStatus.LIVE_BROADCAST_IN_PROGRESS:
        case GrabStatus.FAILED:
            return false;
        default:
            return true;
    }
};

export const getThumbnail = (props) => {
    if (props.grabStatus === GrabStatus.PROCESSING) {
        return PROCESSING_CLIP_THUMBNAIL;
    } else if (props.type === MediaType.CLIP) {
        return props.media.thumbnailUrls[0];
    } else if (props.type === MediaType.IMAGE) {
        return props.media.imageUrl;
    } else {
        return props.media.thumbnailUrl;
    }
};

export const getPlatformPrefix = (props, platformName) => {
    if (platformName === `facebook`) {
        return `fbNative`;
    } else if (
        platformName === `twitternative` ||
        platformName === `twittervmap`
    ) {
        return `twitterNative`;
    } else if (platformName === `theplatform`) {
        return `thePlatformPlayer`;
    } else if (
        platformName === `youtube` ||
        platformName === `brightcove` ||
        platformName === `spredfast` ||
        platformName === `ftp` ||
        platformName === `s3`
    ) {
        return `${platformName}Player`;
    } else if (isRawClip(props.media)) {
        return `raw`;
    }

    return `original`;
};

export const getPlatformCaptionsPrefix = (platformName) => {
    if (platformName === `facebook`) {
        return `fbNative`;
    } else if (platformName === `twitternative`) {
        return `twitterNative`;
    } else if (platformName === `theplatform`) {
        return `thePlatform`;
    } else if (isString(platformName)) {
        return platformName;
    }

    return `original`;
};

export const getPlaybackUrl = (props, platformName) => {
    switch (props.type) {
        case MediaType.IMAGE:
            return props.media.imageUrl;
        case MediaType.GIF:
            return props.media.originalHttpPlaybackUrl;
        case MediaType.CLIP:
            if (platformName === "originalWithCreativeSet") {
                return props.media.httpPlaybackUrl;
            }
            return props.media[
                `${getPlatformPrefix(props, platformName)}HttpPlaybackUrl`
            ];
        default:
            throw new TypeError(`Unexpected MediaType`);
    }
};

export const vodNotSaved = (props) => {
    if (props.type === MediaType.CLIP) {
        return getPlaybackUrl(props).lastIndexOf(`vodNotSaved`) >= 0;
    } else {
        return false;
    }
};

export const getActionButtonInfo = (actionName, props) => {
    if (props.grabStatus === GrabStatus.LIVE_BROADCAST_IN_PROGRESS) {
        return {
            disabled: true,
            message: `${actionName} disabled for live broadcasts in progress`
        };
    } else if (props.grabStatus === GrabStatus.PROCESSING) {
        return {
            disabled: true,
            message: `${actionName} disabled while media is being processed`
        };
    } else if (vodNotSaved(props)) {
        return {
            disabled: true,
            message: `${actionName} disabled when VOD not saved`
        };
    } else if (props.grabStatus === GrabStatus.WAITING_FOR_CS) {
        return {
            disabled: true,
            message: `${actionName} disabled while creative sets are processing`
        };
    } else {
        return {
            disabled: false,
            message: null
        };
    }
};

export const getTypeLabel = (props) => {
    switch (props.type) {
        case MediaType.IMAGE:
        case MediaType.GIF:
            return props.type;
        case MediaType.CLIP:
            return `video`;
        default:
            throw new TypeError(`Unexpected MediaType`);
    }
};

export const isClip = (type) => {
    if (!isString(type)) {
        throw new TypeError(`Unexpected MediaType`);
    } else {
        return type === MediaType.CLIP;
    }
};

export const isImage = (type) => {
    if (!isString(type)) {
        throw new TypeError(`Unexpected MediaType`);
    } else {
        return type === MediaType.IMAGE;
    }
};

export const isGIF = (type) => {
    if (!isString(type)) {
        throw new TypeError(`Unexpected MediaType`);
    } else {
        return type === MediaType.GIF;
    }
};

export const isAudio = (type) => {
    if (!isString(type)) {
        throw new TypeError(`Unexpected MediaType`);
    } else {
        return type === MediaType.AUDIO;
    }
};

export const getVTTUrl = (props, platformName) => {
    if (!isObject(props.media)) {
        return;
    }
    const closedCaptions =
        props.media[`${getPlatformCaptionsPrefix(platformName)}ClosedCaptions`];
    if (isString(closedCaptions)) {
        return JSON.parse(closedCaptions).vtt;
    }
};
